import { debounce } from 'underscore';

import {
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';

import { SubtitleType, VideogularComponent } from './videogular/Videogular.Component';
import { VbBaseMenuComponent } from './shared/VbBaseMenu.Component';

import { ISubtitle } from './ISubtitle';
import { IVbSource } from './IVbSource';
import { IVbTextTrack, TextTrackHelperService } from './TextTrackHelper.Service';
import { LanguageNames } from './LanguageNames';
import { SubtitleService } from './Subtitle.Service';
import { TextTrackKinds } from './TextTrackKinds';
import { TextTrackModes } from './TextTrackModes';

import { ISubtitleOption } from './ISubtitleOption';
import styles from './VbSubtitleMenuButton.module.less';
import { userAgentUtil } from './UserAgentUtil';
import { VgStates } from './videogular/VgStates';



type intenalSubtitleType = (ISubtitleOption | TextTrack) & {
	displayLabel?: string;
};

export const SUBTITLE_OPTION_CAPTIONS: string = 'captions';

@Component({
	selector: 'vb-subtitle-menu-button',
	host: {
		'[class]': 'styles.root'
	},
	template: `
		<div
			[ngClass]="styles.dropdown"
			dropdown
			#dropdown="bs-dropdown"
			[dropup]="true"
			[hidden]="!(isCaptionsAvailable || subtitleOptionsInternal.length)">

			<button
				type="button"
				id="buttonCCMenu"
				aria-controls="dropdownCCMenu"
				dropdownToggle
				[attr.aria-label]="translations.subtitlesAndCCLabel"
				[tooltip]="translations.subtitlesAndCCLabel"
				placement="top"
				[ngClass]="styles.button"
				vbControlBarFocus>
			</button>

			<ul
				[ngClass]="styles.dropdownMenu"
				*dropdownMenu
				id="dropdownCCMenu"
				role="menu"
				aria-labelledby="buttonCCMenu"
				#vbDropdownMenu
				tabindex="0">
				<li
					[hidden]="!isCaptionsAvailable"
					role="menuitem">
					<a
						href="javascript:"
						class="dropdown-item"
						(click)="onManualLangChange(SUBTITLE_OPTION_CAPTIONS)"
						[ngClass]="captionsEnabled ? styles.selected : ''">
						{{ translations.closedCaptions }}
					</a>
				</li>
				<li
					*ngFor="let subtitleOption of subtitleOptionsInternal"
					role="menuitem">
					<a
						href="javascript:"
						class="dropdown-item"
						(click)="onManualLangChange(subtitleOption);"
						[ngClass]="vgApi.activeSubtitle === subtitleOption || (vgApi.activeSubtitle && subtitleOption.mode === 'showing') ? styles.selected : ''">
						{{ subtitleOption.displayLabel }}
					</a>
				</li>
				<li role="menuitem">
					<a
						href="javascript:"
						class="dropdown-item"
						(click)="onManualLangChange(undefined)"
						[ngClass]="!vgApi.activeSubtitle && !captionsEnabled ? styles.selected : ''">
						{{translations.off}}
					</a>
				</li>
			</ul>
		</div>`
})
export class VbSubtitleMenuButtonComponent extends VbBaseMenuComponent implements OnChanges, OnDestroy, OnInit {
	@Input() public activeSource: IVbSource;
	@Input() public currentState: VgStates;
	@Input() public currentTime: number;
	@Input() public isCaptionsAvailable: boolean;
	@Input() public subtitleOptions: ISubtitleOption[];
	@Input('subtitleLangPreferenceOrder') public langOrder: string[];
	@Input() public forcedCCOn: boolean;
	@Input() public translations: any;

	public readonly styles = { ...this.menuButtonStyles, ...styles };
	public readonly SUBTITLE_OPTION_CAPTIONS = SUBTITLE_OPTION_CAPTIONS;

	public isMobile: boolean;
	public subtitleOptionsInternal: intenalSubtitleType[];
	public nativeSubtitles: TextTrack[];
	public captionsEnabled: boolean;

	private isSafari: boolean;
	private isTextTrackType: boolean;
	private onTextTrackListChangeDebounce: () => void;
	private subtitles: ISubtitle[];
	private subscription: Subscription;
	private selectedNativeTextTrack: TextTrack;
	private isManualChanged: boolean;

	constructor(
		private SubtitleService: SubtitleService,
		private TextTrackHelper: TextTrackHelperService,
		public vgApi: VideogularComponent
	) {
		super();
	}

	public ngOnInit(): void {
		this.captionsEnabled = false;
		this.isMobile = userAgentUtil.isMobile;
		this.isSafari = userAgentUtil.isSafari;
		this.onTextTrackListChangeDebounce = debounce(() => this.onTextTrackListChange(), 2000); //may get multiple. plus there's a bit of a delay.

		if (this.isSafari) {
			this.initSafariCaptions();
		}

		this.subscription = this.vgApi.textTracks$.subscribe(result => {
			this.nativeSubtitles = (result || []).filter(track =>
				track.kind === TextTrackKinds.SUBTITLES && track.label !== this.translations.closedCaptions && !track.vb);
			this.updateSubtitlesCollection();
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!this.vgApi.mediaElement) {
			return;
		}

		if (changes.activeSource) {
			this.onActiveSourceChange();
		}

		if (changes.isCaptionsAvailable) {
			this.onIsCaptionsAvailableChange();
		}

		if (changes.subtitleOptions) {
			this.onSubtitleOptionsChange();
		}

		if (changes.currentTime) {
			this.onCurrentTimeChange();
		}

		if (userAgentUtil.isChrome && this.currentState && changes.currentState) {
			this.onPlayerStateChange();
		}
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.subscription?.unsubscribe();
	}

	/**
	 * solving Latest chrome version(86) issue. Once video is stopped
	 * the display subtitle get stuck to player and keep showing after play start.
	 * Hiding subtitle when video is paused/stopped and showing it again when it is played again.
	 */
	private onPlayerStateChange(): void {
		if (!this.vgApi || this.vgApi.isForceHtmlSubtitleRendering) {
			return;
		}

		if (this.currentState !== VgStates.PLAY) {
			this.TextTrackHelper.hideAllTracks(this.vgApi.rawMediaElement);
		} else {
			const activeSubtitle: ISubtitleOption = this.vgApi.activeSubtitle as ISubtitleOption;

			if (activeSubtitle) {
				 if (this.captionsEnabled) {
					this.setCaptionEnabled();
					return;
				 }

				 const textTrack: IVbTextTrack = Array.from(this.vgApi.rawMediaElement.textTracks)
					.find(track => track.label === activeSubtitle.label);

				if (textTrack) {
					textTrack.mode = TextTrackModes.SHOWING;
				}
			}
		}
	}

	private findCurrentSubtitle(currentTime: number): string {
		const currentTimeSec: number = currentTime / 1000; //ms to sec
		let currentSubtitleFindResult: any;

		if (this.subtitles) {
			currentSubtitleFindResult = this.subtitles.find(marker => marker.start <= currentTimeSec && marker.end > currentTimeSec);
		} else if (this.selectedNativeTextTrack) {
			currentSubtitleFindResult = Array.from(this.selectedNativeTextTrack.activeCues || []).find(cue => cue.startTime <= currentTimeSec && cue.endTime > currentTimeSec);
		}

		return currentSubtitleFindResult ? currentSubtitleFindResult.text : null;
	}

	private hideSubtitles(): void {
		this.subtitles = null;
		this.selectedNativeTextTrack = null;
		this.vgApi.setSubtitleTrackInPlugin?.(false, null);

		if (!this.vgApi.isHtmlSubtitleRenderingEnabled) {
			this.TextTrackHelper.hideAllTracks(this.vgApi.rawMediaElement);
		}
	}

	private initSafariCaptions(): void {
		this.vgApi.rawMediaElement.textTracks.addEventListener('addtrack', event => {
			const track: TextTrack = event.track as TextTrack;
			const isSafariAutoCaptionTrack: boolean = track.label === '' && track.kind === TextTrackKinds.CAPTIONS;

			//Hide the auto-added CC track, if captions hasn't been selected. Safari shows it by default.
			if (isSafariAutoCaptionTrack && this.vgApi.activeSubtitle !== SUBTITLE_OPTION_CAPTIONS) {
				track.mode = TextTrackModes.HIDDEN;
			}
		});
	}

	private isNativeVideoPlayback(): boolean {
		return !!this.vgApi.rawMediaElement.textTracks;
	}

	private onActiveSourceChange(): void {
		this.reevaluateSubtitleRenderingMethod();

		if (!this.vgApi.isHtmlSubtitleRenderingEnabled && this.vgApi.activeSubtitle) {
			this.renderThroughTextTrack();
		}

		this.onAutoForcedLangSelect();
	}

	private onCurrentTimeChange(): void {
		if (this.vgApi.activeSubtitle && this.vgApi?.isHtmlSubtitleRenderingEnabled && typeof this.vgApi.currentTime === 'number' && this.vgApi.currentTime >= 0) {
			this.vgApi.currentSubtitle = this.findCurrentSubtitle(this.vgApi.currentTime);
		} else {
			this.vgApi.currentSubtitle = null;
		}
	}

	private onIsCaptionsAvailableChange(): void {
		this.vgApi.isCaptionsAvailable = this.isCaptionsAvailable;
	}

	private onSubtitleOptionsChange(): void {
		const { src, language } = (this.vgApi.activeSubtitle ?? {}) as ISubtitleOption;
		const newSubtitleOption = this.subtitleOptions?.find(subtitle => subtitle.language === language);
		const activeSourceFileChanged = !!src && !!newSubtitleOption && src !== newSubtitleOption?.src;

		if (activeSourceFileChanged) {
			this.vgApi.setActiveSubtitle(newSubtitleOption);
			this.showSubtitles();
		} else {
			this.hideSubtitles();
			this.vgApi.setActiveSubtitle(null);
			this.vgApi.currentSubtitle = null;
		}
		this.updateSubtitlesCollection();
	}

	private updateSubtitlesCollection(): void {
		this.nativeSubtitles = this.nativeSubtitles || [];
		this.subtitleOptions = this.subtitleOptions || [];

		this.subtitleOptionsInternal = [...this.subtitleOptions, ...this.nativeSubtitles];

		this.subtitleOptionsInternal = this.subtitleOptionsInternal
			.map(subtitle => {
				subtitle.displayLabel = LanguageNames[subtitle.language.toLowerCase()] || subtitle.label;
				return subtitle;
			})
			.filter(Boolean);
		const activeSubtitle = this.subtitleOptionsInternal.find(subtitle => (subtitle as TextTrack).mode === TextTrackModes.SHOWING);

		// if by default any subtitle is enabled in playlist file.
		if (activeSubtitle) {
			this.vgApi.setActiveSubtitle(activeSubtitle);
		}
	}

	private onTextTrackListChange(): void {
		const textTracks: TextTrackList | any[] = this.vgApi.rawMediaElement.textTracks || [];

		//find the subtitle being shown, if any
		const showingTrack: IVbTextTrack = Array.from(textTracks as TextTrackList).find(currentTextTrack => currentTextTrack.mode === TextTrackModes.SHOWING);
		const showingLabel: string = showingTrack && showingTrack.label;

		//update the activeSubtitle value to match the change
		this.vgApi.setActiveSubtitle(
			typeof showingLabel === 'string' ? (
				showingTrack.vb ? this.subtitleOptions.find(option => option.label === showingLabel) :
				this.captionsEnabled ? SUBTITLE_OPTION_CAPTIONS :
				showingTrack
			) : null);
	}

	private reevaluateSubtitleRenderingMethod(): void {
		const is360Enabled: boolean = this.vgApi.activeSource && this.vgApi.activeSource.is360Enabled;

		this.vgApi.isHtmlSubtitleRenderingEnabled =
			// check if 360 video is enabled
			is360Enabled ||

			// workaround Safari Mac text track bug where its Auto default selection breaks the TextTrack API surfacing of tracks
			userAgentUtil.isSafariMac ||

			userAgentUtil.isFirefox ||

			// IE and legacy Edge have only basic support for VTT. Can't style, so you're stuck with what is configured through the browser's accessibility settings (defaults are questionable)
			userAgentUtil.isIeOrLegacyEdge ||

			this.vgApi.isForceHtmlSubtitleRendering ||

			!this.TextTrackHelper.isNativeTextTrackSupport ||

			!this.isNativeVideoPlayback();
	}

	private renderThroughTextTrack(): void {
		const activeSubtitle: ISubtitleOption = this.vgApi.activeSubtitle as ISubtitleOption;

		//attempt to locate an existing track. If it doesn't already exist, create a new track
		const textTrack: IVbTextTrack = this.TextTrackHelper.getOrCreateTrack(this.vgApi.rawMediaElement as HTMLVideoElement, TextTrackKinds.CAPTIONS, activeSubtitle.label);

		//frustratingly the TextTrack spec deliberately leaves out track removal (they were only thinking about in-band tracks and not dynamic), so we have to reuse
		//remove all cues
		this.TextTrackHelper.clearCues(textTrack);

		//add cues to the track
		this.subtitles?.forEach(subtitle => {
			const cue = this.TextTrackHelper.addCue(textTrack, subtitle.start, subtitle.end, subtitle.text);
		});

		//surface the text track
		textTrack.mode = TextTrackModes.SHOWING;
	}

	private setSafariCaptionsEnabled(isEnabled: boolean): void {
		const track: IVbTextTrack = this.TextTrackHelper.getTrack(this.vgApi.rawMediaElement, TextTrackKinds.CAPTIONS, '', false);

		if (track) {
			track.mode = isEnabled ? TextTrackModes.SHOWING : TextTrackModes.HIDDEN;
		}
	}

	private showSubtitles(): void {
		const src: string = (this.vgApi.activeSubtitle as ISubtitleOption).src;

		this.SubtitleService.getParsedSubtitle(src)
			.then(result => {
				this.subtitles = result;

				if (!this.vgApi.isHtmlSubtitleRenderingEnabled) {
					this.renderThroughTextTrack();
				}
			})
			.catch(err => console.error('Unable to load subtitles from ', err, src));
	}

	public onManualLangChange(subtitleOption: SubtitleType): void {
		this.isManualChanged = true;
		this.updateSelection(subtitleOption);
	}

	private onAutoForcedLangSelect(): void {
		if (this.isManualChanged || !this.activeSource || (!this.forcedCCOn && !this.langOrder?.length)) {
			return;
		}
		const activeSubtitle = this.getCCOrSubtitleByPreferanceOrder();

		if (activeSubtitle) {
			this.updateSelection(activeSubtitle);
		}
	}

	public updateSelection(subtitleOption: SubtitleType): void {
		this.captionsEnabled = subtitleOption === this.SUBTITLE_OPTION_CAPTIONS;
		this.isTextTrackType = (subtitleOption as TextTrack)?.kind === TextTrackKinds.SUBTITLES;

		if (this.isMobile || subtitleOption !== this.vgApi.activeSubtitle) { //change
			this.vgApi.setActiveSubtitle(subtitleOption);
			const textTracks: TextTrackList = this.vgApi.rawMediaElement.textTracks;

			//needed only for native case.
			if (userAgentUtil.isSafariIos && textTracks && textTracks.removeEventListener) {
				textTracks.removeEventListener('change', this.onTextTrackListChangeDebounce);
			}

			if (subtitleOption && !this.captionsEnabled && !this.isTextTrackType) {
				this.hideSubtitles();
				this.reevaluateSubtitleRenderingMethod();
				this.showSubtitles();
			} else if (this.isTextTrackType) {
				this.reevaluateSubtitleRenderingMethod();
				this.onUpdateTextTrackSelection(subtitleOption as TextTrack);
			} else {
				this.hideSubtitles();
			}

			this.setCaptionEnabled();

			//attach listener to handle native player control changes to the selection
			if (userAgentUtil.isSafariIos && textTracks && textTracks.addEventListener) {
				textTracks.addEventListener('change', this.onTextTrackListChangeDebounce);
			}
		}
	}

	private onUpdateTextTrackSelection(textTrack: TextTrack): void {
		this.TextTrackHelper.hideAllTracks(this.vgApi.rawMediaElement);
		this.selectedNativeTextTrack = this.TextTrackHelper.getTrack(this.vgApi.rawMediaElement, TextTrackKinds.SUBTITLES, textTrack.label);
		this.vgApi.setSubtitleTrackInPlugin?.(!this.vgApi.isHtmlSubtitleRenderingEnabled, textTrack);

		if (!this.vgApi.isHtmlSubtitleRenderingEnabled) {
			this.selectedNativeTextTrack.mode = TextTrackModes.SHOWING;
		}
	}

	private getCCOrSubtitleByPreferanceOrder(): SubtitleType {
		if (!this.forcedCCOn) {
			return;
		}

		if (this.isCaptionsAvailable) {
			return SUBTITLE_OPTION_CAPTIONS;
		}

		if (!this.langOrder?.length || !this.subtitleOptionsInternal?.length) {
			return;
		}
		let activeSubtitle;

		for (const lang of this.langOrder) {
			//find exact matching.
			activeSubtitle = this.subtitleOptionsInternal.find(sub => lang === sub.language);

			if (!activeSubtitle) {
				//take the base part. lang may have hyphen as well;
				const langBasePart = lang.split('-')[0];
				for(const sub of this.subtitleOptionsInternal) {
					//check if lang exact base part is there.
					if (sub.language === langBasePart) {
						activeSubtitle = sub;
						break;
					} else if (sub.language.startsWith(langBasePart)) { //pick lang from the same group;
						activeSubtitle = sub;
					}
				}
			}

			if (activeSubtitle) {
				break;
			}
		}
		return activeSubtitle ?? this.subtitleOptionsInternal[0]; //nothing found then return the first one.
	}

	private setCaptionEnabled(): void {
		if (this.vgApi.setCaptionsEnabled) {
			this.vgApi.setCaptionsEnabled(this.captionsEnabled);
		} else if (this.isSafari) {
			this.setSafariCaptionsEnabled(this.captionsEnabled);
		}
	}
}
